import { xpForLevel as contentXp } from "@zend/content";

export const MAX_STARS = 5;
export const MAX_ABILITY_LEVEL = 10;

export function xpForLevel(level: number): number {
  return contentXp(level);
}

/** Unit level cap: min(30 + 20 × stars, 10 + 4 × HQ); both reach 130 at 5★ / HQ 30. */
export function levelCap(stars: number, hqLevel: number): number {
  return Math.min(30 + 20 * stars, 10 + 4 * hqLevel);
}

const STAR_COSTS = [50, 100, 250, 500, 1000];

/** Fragments needed to reach `star` (1..5) from star−1. */
export function starCostFragments(star: number): number {
  const c = STAR_COSTS[star - 1];
  if (c === undefined) throw new RangeError(`no star ${star}`);
  return c;
}

export const FRAGMENTS_PER_DUPLICATE = 10;

const ABILITY_COSTS = [5, 10, 20, 35, 55, 80, 110, 150, 200];

/** Compacted Data Cores to raise an ability to `toLevel` (2..10; 11 costs the same as 10). */
export function abilityUpgradeCost(toLevel: number): number {
  const c = ABILITY_COSTS[Math.min(toLevel, MAX_ABILITY_LEVEL) - 2];
  if (c === undefined || toLevel < 2) throw new RangeError(`no ability level ${toLevel}`);
  return c;
}

/** min(10, floor(hangar / 3) + 1), plus one at five stars. */
export function abilityLevelCap(hangarLevel: number, stars: number): number {
  return Math.min(MAX_ABILITY_LEVEL, Math.floor(hangarLevel / 3) + 1) + (stars >= MAX_STARS ? 1 : 0);
}

/** Effect magnitude multiplier for an ability level (+8 % per level above 1). */
export function abilityMagnitude(level: number): number {
  return 1 + 0.08 * (level - 1);
}

export interface XpState {
  level: number;
  xp: number;
}

/** Applies XP, levelling up while below the cap; surplus XP is kept for later cap raises. */
export function applyXp(current: XpState, gained: number, cap: number): XpState & { levelsGained: number } {
  let level = current.level;
  let xp = current.xp + Math.max(0, Math.floor(gained));
  let levelsGained = 0;
  while (level < cap && xp >= xpForLevel(level)) {
    xp -= xpForLevel(level);
    level++;
    levelsGained++;
  }
  return { level, xp, levelsGained };
}

/** Total XP required to go from level 1 to `level`. */
export function cumulativeXp(level: number): number {
  let total = 0;
  for (let l = 1; l < level; l++) total += xpForLevel(l);
  return total;
}
